import React, { useMemo, useState } from "react";
import Button from "../Button/Button";
import { SelectionCard, SelectionRadioCard } from "../Cards/Cards";
import CustomSelect from "../CustomSelect/CustomSelect";
import InputBox from "../InputBox/InputBox";
import PreviewBar from "../PreviewBar/PreviewBar";
import { isValidObject } from "../../Services/validators";

function CreateLocation(props) {
  const [step, setStep] = useState("selection");

  const [formData, setFormData] = useState({
    _geoloc: { lat: "", lng: "" },
    access: "public",
    category: "",
    condition: "inUse",
    createdAt: +new Date(),
    isElectricityAvailable: true,
    isWaterAvailable: true,
    location: "",
    milestone: "",
    operationalStatus: {
      state: "Active",
      reason: ""
    },
    PCTNo: "",
    seats: {
      other: {
        bathing: "",
        urinals: ""
      },
      pct: {
        indian: {
          disabled: "0",
          female: "0",
          male: "0",
          nonBinary: "0"
        },
        western: {
          disabled: "0",
          female: "0",
          male: "0",
          nonBinary: "0"
        }
      }
    },
    sewageConnectivity: "",
    ward: "",
    zone: ""
  });

  const handleDisable = useMemo(() => {
    const isValidFloat = (value) => {
      const num = parseFloat(value);
      return !isNaN(num) && num !== 0;
    };

    const requiredFields = [
      formData._geoloc.lat,
      formData._geoloc.lng,
      formData.category,
      formData.location,
      formData.milestone,
      formData.PCTNo,
      formData.sewageConnectivity,
      formData.ward,
      formData.zone
    ];

    const urinalCounts = [
      parseInt(formData.seats.pct.indian.male, 10) || 0,
      parseInt(formData.seats.pct.indian.female, 10) || 0,
      parseInt(formData.seats.pct.indian.disabled, 10) || 0,
      parseInt(formData.seats.pct.indian.nonBinary, 10) || 0,
      parseInt(formData.seats.pct.western.male, 10) || 0,
      parseInt(formData.seats.pct.western.female, 10) || 0,
      parseInt(formData.seats.pct.western.disabled, 10) || 0,
      parseInt(formData.seats.pct.western.nonBinary, 10) || 0
    ];

    const isValidLocationDetails =
      parseInt(formData.milestone, 10) > 0 ||
      parseInt(formData.PCTNo, 10) > 0 ||
      parseInt(formData.ward, 10) > 0 ||
      parseInt(formData.zone, 10) > 0;

    const isGeolocValid =
      isValidFloat(formData._geoloc.lat) && isValidFloat(formData._geoloc.lng);

    const isRequiredFieldEmpty = requiredFields.some(
      (field) => field === "" || field === null || field === undefined
    );

    const isUrinalAvailableButEmpty =
      formData.seats.other.urinals === "Available" &&
      !urinalCounts.some((count) => count > 0);

    const IsSeatOptionNotSelected =
      formData.seats.other.urinals.trim() === "" ||
      formData.seats.other.bathing.trim() === "";

    return (
      isRequiredFieldEmpty ||
      isUrinalAvailableButEmpty ||
      IsSeatOptionNotSelected ||
      !IsPCTExist(formData.PCTNo).status ||
      !isValidLocationDetails ||
      !isGeolocValid
    );
    // eslint-disable-next-line
  }, [formData]);

  function IsPCTExist(value) {
    if (isValidObject(props.locations?.data)) {
      const pctExists = Object.values(props.locations?.data).find(
        (location) => location.PCTNo === value
      );
      if (pctExists) {
        return {
          status: false,
          message: "PCT already exists"
        };
      }
    }

    return { status: true, PCTNo: formData.PCTNo };
  }

  const handleCreateLocation = () => {
    const updatedFormData = {
      ...formData,
      seats: {
        ...formData.seats,
        pct:
          formData.seats.other.urinals === "Unavailable"
            ? {
                indian: { disabled: 0, female: 0, male: 0, nonBinary: 0 },
                western: { disabled: 0, female: 0, male: 0, nonBinary: 0 }
              }
            : formData.seats.pct
      }
    };
    props.createNewLocation(updatedFormData);
    props.navigate("locations/overview");
  };

  return (
    <PreviewBar
      title="New Location"
      onCloseClick={() => {
        props.navigate("/locations/overview");
      }}
    >
      <div className="inherit-parent-height">
        {step === "selection" && (
          <div className="inherit-parent-height">
            <CategorySelection
              setFormData={setFormData}
              formData={formData}
              continueClick={() => {
                setStep("creation");
              }}
            />
          </div>
        )}

        {step === "creation" && (
          <div className="inherit-parent-height display-flex flex-justify-content-space-between flex-direction-column overflow-hidden">
            <div
              style={{
                height: "calc(100% - 56px)"
              }}
              className="overflow-scroll"
            >
              <SelectionCard
                title="CATEGORY"
                value={formData.category}
                changeOnClick={() => {
                  setStep("selection");
                }}
              />

              <BasicInfoForm
                formData={formData}
                setFormData={setFormData}
                IsPCTExist={IsPCTExist}
                onChange={(e) => {
                  if (e.target.name === "lat" || e.target.name === "lng") {
                    setFormData({
                      ...formData,
                      _geoloc: {
                        ...formData._geoloc,
                        [e.target.name]: parseFloat(e.target.value)
                      }
                    });
                  } else {
                    setFormData({
                      ...formData,
                      [e.target.name]: e.target.value
                    });
                  }
                }}
              />

              <div
                style={{
                  paddingTop: "42px"
                }}
                className="font-color-tertiaryColor6"
              >
                FACILITY
              </div>

              <FacilityForm formData={formData} setFormData={setFormData} />
              {formData.seats.other.urinals === "Available" && (
                <FacilityCountForm
                  formData={formData}
                  setFormData={setFormData}
                  onChange={(e) => {
                    const seatType = e.target.name
                      .toLocaleLowerCase()
                      .includes("indian")
                      ? "indian"
                      : "western";
                    setFormData({
                      ...formData,
                      seats: {
                        ...formData.seats,
                        pct: {
                          ...formData.seats.pct,
                          [seatType]: {
                            ...formData.seats.pct[seatType],
                            [e.target.ariaLabel]:
                              e.target.value &&
                              !isNaN(parseInt(e.target.value, 10))
                                ? parseInt(e.target.value, 10)
                                : ""
                          }
                        }
                      }
                    });
                  }}
                />
              )}
            </div>

            <div
              style={{ bottom: 0, zIndex: 1 }}
              className="padding-top-larger"
            >
              <Button
                type="button"
                disabled={handleDisable}
                text="Create new location"
                loading={props.createNewLocationLoading}
                onClick={handleCreateLocation}
                data-cy="CREATE-LOCATION-CONFIRM-BUTTON"
              />
            </div>
          </div>
        )}
      </div>
    </PreviewBar>
  );
}

export default CreateLocation;

const CategorySelection = (props) => {
  const categoryData = [
    {
      title: "Community",
      description: "test description"
    },
    {
      title: "Institutional",
      description: "test description"
    },
    {
      title: "Public space",
      description: "test description"
    },
    {
      title: "Transit area",
      description: "test description"
    }
  ];

  const refactorCategory = (key) => {
    switch (key) {
      case "Transit area":
        return "transitArea";
      case "Public space":
        return "publicSpace";
      default:
        return key.toLocaleLowerCase();
    }
  };
  return (
    <div className="display-flex flex-direction-column flex-justify-content-space-between inherit-parent-height">
      <div>
        <div className="padding-bottom-large font-color-tertiaryColor6">
          CATEGORY
        </div>
        {categoryData.map((data, i) => (
          <div
            key={i}
            onClick={() => {
              props.setFormData({
                ...props.formData,
                category: refactorCategory(data.title)
              });
            }}
            className="padding-bottom-large"
          >
            <SelectionRadioCard
              data={data}
              selected={
                props.formData.category === refactorCategory(data.title)
              }
            />
          </div>
        ))}
      </div>
      <div>
        <Button
          type="button"
          data-cy="CATEGORY-CONFIRM-BUTTON"
          disabled={props.formData.category ? false : true}
          text="Continue"
          onClick={props.continueClick}
        />
      </div>
    </div>
  );
};

const SectionSeparator = (props) => {
  return (
    <div
      style={{
        marginTop: "24px",
        marginBottom: "24px"
      }}
      className="flex-center-children-vertically"
    >
      <hr
        style={{ width: "100%", border: "1px solid rgba(255, 255, 255, 0.08)" }}
        className="display-inline-block border-top-left-right-none flex-basis-1 "
      />
      <span className=" margin-horizontal-default display-inline-block font-color-tertiaryColor white-space-nowrap padding-left-larger padding-right-larger">
        {props.title}
      </span>
      <hr
        style={{ width: "100%", border: "1px solid rgba(255, 255, 255, 0.08)" }}
        className="display-inline-block border-top-left-right-none flex-basis-1 "
      />
    </div>
  );
};

const BasicInfoForm = (props) => {
  return (
    <>
      <form onChange={props.onChange} className="padding-top-larger">
        <InputBox
          className="inherit-parent-width"
          name="location"
          label="NAME"
          type="text"
          placeholder="Enter Location Name"
          removeResponsive={true}
          value={props.formData.location}
          fontColor="font-color-tertiaryColor"
          labelFontColor="font-color-tertiaryColor"
          data-cy="LOCATION-FIELD"
          required
        />
        <div className="display-flex padding-top-default">
          <InputBox
            name="PCTNo"
            label="PCT NUMBER"
            type="text"
            placeholder="Enter PCT #"
            size="smaller"
            removeResponsive={true}
            value={props.formData.PCTNo}
            validation={(value) => {
              return props.IsPCTExist(value);
            }}
            fontColor="font-color-tertiaryColor"
            labelFontColor="font-color-tertiaryColor"
            data-cy="PCT-NO-FIELD"
            required
          />
          <div style={{ padding: "16px", height: "100%" }} />
          <InputBox
            name="milestone"
            label="MILESTONE"
            type="number"
            size="smaller"
            placeholder="Enter Milestone"
            removeResponsive={true}
            value={props.formData.milestone}
            fontColor="font-color-tertiaryColor"
            labelFontColor="font-color-tertiaryColor"
            data-cy="MILESTONE-FIELD"
            required
          />
        </div>

        <div className="display-flex padding-top-default">
          <InputBox
            name="ward"
            label="WARD"
            type="number"
            size="small"
            placeholder="Enter Ward"
            removeResponsive={true}
            value={props.formData.ward}
            fontColor="font-color-tertiaryColor"
            labelFontColor="font-color-tertiaryColor"
            data-cy="WARD-FIELD"
            required
          />
          <div style={{ padding: "16px", height: "100%" }} />
          <InputBox
            name="zone"
            label="ZONE"
            type="number"
            size="small"
            placeholder="Enter Zone"
            removeResponsive={true}
            value={props.formData.zone}
            fontColor="font-color-tertiaryColor"
            labelFontColor="font-color-tertiaryColor"
            data-cy="ZONE-FIELD"
            required
          />
        </div>

        <div className="display-flex padding-top-default">
          <InputBox
            name="lat"
            label="LATITUDE"
            type="number"
            size="small"
            placeholder="Enter Latitude"
            removeResponsive={true}
            value={props.formData._geoloc.lat}
            fontColor="font-color-tertiaryColor"
            labelFontColor="font-color-tertiaryColor"
            data-cy="LATITUDE-FIELD"
            required
          />
          <div style={{ padding: "16px", height: "100%" }} />
          <InputBox
            name="lng"
            label="LONGITUDE"
            type="number"
            size="small"
            placeholder="Enter Longitude"
            removeResponsive={true}
            value={props.formData._geoloc.lng}
            fontColor="font-color-tertiaryColor"
            labelFontColor="font-color-tertiaryColor"
            data-cy="LONGITUDE-FIELD"
            required
          />
        </div>
      </form>
    </>
  );
};

const FacilityForm = (props) => {
  return (
    <>
      <form onChange={props.onChange} className="padding-top-larger">
        <CustomSelect
          data-cy="SEWAGE-CONNECTIVITY-FIELD"
          options={[
            { title: "UGD", key: "UGD" },
            { title: "unspecified", key: "unspecified" }
          ]}
          placeholder="Select Sewage Connectivity"
          handleSelectedData={(data) => {
            props.setFormData({
              ...props.formData,
              sewageConnectivity: data
            });
          }}
          search={false}
          selected={props.formData.sewageConnectivity}
          removeResponsive={true}
          label="SEWAGE CONNECTIVITY"
          value={props.formData.sewageConnectivity}
          fontColor="font-color-tertiaryColor"
          labelFontColor="font-color-tertiaryColor"
        />

        <div className="display-flex padding-top-default padding-bottom-large">
          <CustomSelect
            data-cy="BATHING-FIELD"
            options={[
              { title: "Available", key: "Available" },
              { title: "Unavailable", key: "Unavailable" }
            ]}
            placeholder="Select Availability"
            handleSelectedData={(data) => {
              props.setFormData({
                ...props.formData,
                seats: {
                  ...props.formData.seats,
                  other: {
                    ...props.formData.seats.other,
                    bathing: data
                  }
                }
              });
            }}
            search={false}
            selected={props.formData.seats.other.bathing}
            removeResponsive={true}
            label="BATH ROOM"
            value={props.formData.seats.other.bathing}
            fontColor="font-color-tertiaryColor"
            labelFontColor="font-color-tertiaryColor"
          />

          <div style={{ padding: "16px", height: "100%" }} />

          <CustomSelect
            data-cy="URINALS-FIELD"
            options={[
              { title: "Available", key: "Available" },
              { title: "Unavailable", key: "Unavailable" }
            ]}
            placeholder="Select Availability"
            handleSelectedData={(data) => {
              props.setFormData({
                ...props.formData,
                seats: {
                  ...props.formData.seats,
                  other: {
                    ...props.formData.seats.other,
                    urinals: data
                  }
                }
              });
            }}
            search={false}
            selected={props.formData.seats.other.urinals}
            removeResponsive={true}
            label="URINAL"
            value={props.formData.seats.other.urinals}
            fontColor="font-color-tertiaryColor"
            labelFontColor="font-color-tertiaryColor"
          />
        </div>
      </form>
    </>
  );
};

const FacilityCountForm = (props) => {
  return (
    <>
      <form onChange={props.onChange} className="padding-top-larger">
        <SectionSeparator title="# OF URINAL (INDIAN)" />

        <div className="display-flex">
          <InputBox
            data-cy="INDIAN-MALE-FIELD"
            fontColor="font-color-tertiaryColor"
            ariaLabel="male"
            label="MALE"
            labelFontColor="font-color-tertiaryColor"
            name="seatsPCTIndianMale"
            removeResponsive={true}
            placeholder="# OF SEATS"
            required
            size="smaller"
            type="text"
            value={props.formData.seats.pct.indian.male}
          />
          <div style={{ padding: "8px", height: "100%" }} />
          <InputBox
            data-cy="INDIAN-FEMALE-FIELD"
            fontColor="font-color-tertiaryColor"
            ariaLabel="female"
            label="FEMALE"
            placeholder="# OF SEATS"
            labelFontColor="font-color-tertiaryColor"
            name="seatsPCTIndianFemale"
            removeResponsive={true}
            required
            size="smaller"
            type="text"
            value={props.formData.seats.pct.indian.female}
          />
          <div style={{ padding: "8px", height: "100%" }} />
          <InputBox
            data-cy="INDIAN-DISABLED-FIELD"
            fontColor="font-color-tertiaryColor"
            ariaLabel="disabled"
            label="DISABLED"
            placeholder="# OF SEATS"
            labelFontColor="font-color-tertiaryColor"
            name="seatsPCTIndianDisabled"
            removeResponsive={true}
            required
            size="smaller"
            type="text"
            value={props.formData.seats.pct.indian.disabled}
          />
          <div style={{ padding: "8px", height: "100%" }} />
          <InputBox
            data-cy="INDIAN-NON-BINARY-FIELD"
            fontColor="font-color-tertiaryColor"
            ariaLabel="nonBinary"
            label="NON-BINARY"
            placeholder="# OF SEATS"
            labelFontColor="font-color-tertiaryColor"
            name="seatsPCTIndianNonBinary"
            removeResponsive={true}
            required
            size="smaller"
            type="text"
            value={props.formData.seats.pct.indian.nonBinary}
          />
        </div>

        <SectionSeparator title="# OF URINAL (WESTERN)" />

        <div className="display-flex">
          <InputBox
            data-cy="WESTERN-MALE-FIELD"
            fontColor="font-color-tertiaryColor"
            ariaLabel="male"
            label="MALE"
            placeholder="# OF SEATS"
            labelFontColor="font-color-tertiaryColor"
            name="seatsPCTWesternMale"
            removeResponsive={true}
            required
            size="smaller"
            type="text"
            value={props.formData.seats.pct.western.male}
          />
          <div style={{ padding: "8px", height: "100%" }} />
          <InputBox
            data-cy="WESTERN-FEMALE-FIELD"
            fontColor="font-color-tertiaryColor"
            ariaLabel="female"
            placeholder="# OF SEATS"
            label="FEMALE"
            labelFontColor="font-color-tertiaryColor"
            name="seatsPCTWesternFemale"
            removeResponsive={true}
            required
            size="smaller"
            type="text"
            value={props.formData.seats.pct.western.female}
          />
          <div style={{ padding: "8px", height: "100%" }} />
          <InputBox
            data-cy="WESTERN-DISABLED-FIELD"
            fontColor="font-color-tertiaryColor"
            ariaLabel="disabled"
            label="DISABLED"
            placeholder="# OF SEATS"
            labelFontColor="font-color-tertiaryColor"
            name="seatsPCTWesternDisabled"
            removeResponsive={true}
            required
            size="smaller"
            type="text"
            value={props.formData.seats.pct.western.disabled}
          />
          <div style={{ padding: "8px", height: "100%" }} />
          <InputBox
            data-cy="WESTERN-NON-BINARY-FIELD"
            fontColor="font-color-tertiaryColor"
            ariaLabel="nonBinary"
            label="NON-BINARY"
            placeholder="# OF SEATS"
            labelFontColor="font-color-tertiaryColor"
            name="seatsPCTWesternNonBinary"
            removeResponsive={true}
            required
            size="smaller"
            type="text"
            value={props.formData.seats.pct.western.nonBinary}
          />
        </div>
      </form>
    </>
  );
};

// const check = (second) => { third }
